import type { FaceOnCompleteCallbackImage } from '@innovatrics/dot-face-auto-capture';

import { Spacer } from '@dot/design-system';
import { Step } from '@repo/common';
import { useTranslation } from 'react-i18next';

import { onAutoCaptureError } from '../utils/error';

import { FaceCamera } from './components/camera';
import { FaceCameraUi } from './components/camera-ui';
import { FaceInfoOverlay } from './components/info-overlay';
import { useUploadFace } from './hooks/use-upload-face';

import { CaptureServerErrorOverlay } from '@/components/CaptureServerErrorOverlay/CaptureServerErrorOverlay';
import { ManualMessageUpload } from '@/components/ManualUpload/ManualMessageUpload';
import { SecuredByBadge } from '@/components/SecuredByBadge/SecuredByBadge';
import { CaptureWrapper } from '@/components/capture-wrapper';
import { config } from '@/config';
import { withStep } from '@/hoc/with-step';
import { useAppContext } from '@/providers/app-provider/context';
import * as GSC from '@/styles/GlobalStyles';
import { getPublicAsset } from '@/utils/utils';

const FACE_STEP_VALUES = [Step.FACE, Step.AGE_FACE] as const;

export const FaceCapture = withStep({ only: [...FACE_STEP_VALUES] })(() => {
  const { t } = useTranslation();
  const { state } = useAppContext();
  const { captureStep, retryCapture, selfie, serverErrorFailReason, serverErrorMessage, serverErrorRetryable, uploadSelfie } =
    useUploadFace();

  async function handleComplete({ image }: FaceOnCompleteCallbackImage, content: Uint8Array) {
    await uploadSelfie(image, content);
  }

  return (
    <>
      <GSC.RelativeContainer>
        <CaptureWrapper>
          <GSC.CaptureContainer>
            <FaceCamera
              assetsDirectoryPath={getPublicAsset('/')}
              camera={{ facingMode: 'user' }}
              onComplete={handleComplete}
              onError={onAutoCaptureError}
            />
            <FaceCameraUi
              appStateInstructions={{ loading: { text: t('common.state.loading') }, waiting: { visible: false } }}
              control={{ showCameraButtons: true }}
              instructions={{
                candidate_selection: t('steps.faceCapture.instructions.candidateSelection'),
                face_centering: t('steps.faceCapture.instructions.faceCentering'),
                face_too_close: t('steps.faceCapture.instructions.faceTooClose'),
                face_too_far: t('steps.faceCapture.instructions.faceTooFar'),
                face_not_present: t('steps.faceCapture.instructions.faceNotPresent'),
                sharpness_too_low: t('steps.faceCapture.instructions.sharpnessTooLow'),
                brightness_too_high: t('steps.faceCapture.instructions.brightnessTooHigh'),
                brightness_too_low: t('steps.faceCapture.instructions.brightnessTooLow'),
                device_pitched: t('steps.faceCapture.instructions.devicePitched'),
              }}
            />
          </GSC.CaptureContainer>
        </CaptureWrapper>
        <FaceInfoOverlay captureStep={captureStep} image={selfie} />
        {serverErrorFailReason && (
          <CaptureServerErrorOverlay
            failReason={serverErrorFailReason}
            message={serverErrorMessage}
            onRetry={retryCapture}
            retryable={serverErrorRetryable}
            unverifiedUrl={state.customerData?.redirect?.unverifiedUrl ?? ''}
          />
        )}
      </GSC.RelativeContainer>
      <Spacer $top={1}>
        <SecuredByBadge />
      </Spacer>
      {config.photoUploadEnabled && (
        <ManualMessageUpload
          id="face_manual_upload"
          onComplete={(content) => {
            handleComplete({ image: new Blob(['']), data: {} as FaceOnCompleteCallbackImage['data'] }, content);
          }}
        />
      )}
    </>
  );
});
